import { Schema } from "effect"
import { Actor, ContentMeta, EntityRef, Evidence, Health, NodeRef, Principal, RecordMeta } from "../common"
import { DocMeta } from "../documents"
import { MergeView, PrView } from "../github"
import {
  AgentId,
  AreaId,
  ArtifactId,
  CommentId,
  IntegrationId,
  ItemId,
  Markdown,
  MemoryItemId,
  Millis,
  ProjectId,
  ProposalId,
  RunId,
  Sha256,
  ShortRef,
  Slug,
  UserId,
} from "../ids"
import { ArtifactKind } from "./item"
import { MessageParty } from "./thread"

// #region memory
/** Scope decides WHETHER an item is loaded. Narrower wins a conflict, except `locked` items from a wider scope. */
export const MemoryScope = Schema.TaggedUnion({
  user: { userId: UserId }, // personal: never shared, loaded only for agents acting on behalf of this user
  workspace: {},
  project: { projectId: ProjectId },
  area: { projectId: ProjectId, areaId: AreaId },
  agent: { agentId: AgentId }, // the notebook
  item: { projectId: ProjectId, itemId: ItemId }, // archived when the item closes
})
export type MemoryScope = typeof MemoryScope.Type

export const MemoryType = Schema.Literals([
  "rule",
  "preference",
  "fact",
  "decision",
  "learning",
  "playbook",
  "journal",
])

/** Type decides HOW an item is loaded and who may change it. Type-specific fields live here. */
export const MemoryContent = Schema.TaggedUnion({
  rule: {}, // always loaded for its scope; humans write, agents propose (always a card, wave C)
  /**
   * Wave C (decision 0071, C1): a preference is project memory ABOUT a person (`about`); anyone writes it and that person
   * is told (Changed for you). Absent `about`: a user-scope preference from before wave C.
   */
  preference: { about: Schema.optionalKey(UserId) },
  fact: { summary: Schema.String, evidence: Schema.Array(Evidence) }, // summary always, body on demand
  decision: { decidedAt: Millis, links: Schema.Array(NodeRef), alternatives: Schema.Array(Schema.String) }, // on trigger
  learning: {
    evidence: Schema.NonEmptyArray(Evidence),
    confidence: Schema.Literals(["low", "medium", "high"]),
    revisitAt: Millis,
  }, // on trigger; agents propose, the lead curates
  playbook: { skillName: Slug }, // on demand, rendered as a skill
  journal: { runId: Schema.NullOr(RunId) }, // never loaded; recall/search only; written by the system
})

export const MemoryTriggers = Schema.Struct({
  tags: Schema.Array(Slug),
  paths: Schema.Array(Schema.String),
  integrations: Schema.Array(IntegrationId),
})

/**
 * Wave C (decision 0071): where a memory came from. A chat message (the agent's shared chat and the message id), a
 * comment, a record by its ref (DIC-41, O-14, P-58, G-1, M-7), a run, or typed by a person. Decide checks that it exists
 * in this project.
 */
export const MemorySource = Schema.TaggedUnion({
  message: {
    agentId: AgentId,
    messageId: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  },
  comment: { commentId: CommentId },
  record: { ref: ShortRef },
  run: { runId: RunId },
  typed: {},
})
export type MemorySource = typeof MemorySource.Type

/** What a proposal carries and what a direct human edit writes. */
export const MemoryDraft = Schema.Struct({
  scope: MemoryScope,
  title: Schema.String,
  body: Markdown,
  content: MemoryContent,
  triggers: MemoryTriggers,
  /** Wider-scope rule nothing narrower may override (workspace legal rules). Humans only. */
  locked: Schema.Boolean,
  /**
   * Wave C (decision 0071): where it came from. Optional key, so drafts and proposal bodies from before hash the same.
   * Kept on revise and restore (it lands on the item through `MemoryItem`'s spread).
   */
  source: Schema.optionalKey(MemorySource),
  /**
   * Decision 0146 (memory phase 2): when the claim is true in the world, if it says so ("from 1 October", "until the
   * launch"). Absent = from when it was written, with no end. Optional keys: drafts from before hash the same.
   */
  validFrom: Schema.optionalKey(Millis),
  validTo: Schema.optionalKey(Millis),
})

/**
 * Stored in the scope owner's DO: user → User DO, workspace → Workspace DO, project/area/item → Project DO,
 * agent → Agent DO. Version history = events. Load statistics are a projection over run manifests, not a field.
 */
export const MemoryItem = Schema.Struct({
  ...RecordMeta, // user scope forces visibility "private"
  ...ContentMeta,
  ...MemoryDraft.fields,
  id: MemoryItemId,
  revision: Schema.Int,
  status: Schema.Literals(["accepted", "archived", "revoked"]), // "proposed" lives in the ledger, not here
  tainted: Schema.Boolean, // copied from the source run; tainted items are always rendered as quoted reference material
  reviewedBy: Schema.NullOr(Actor),
  proposalId: Schema.NullOr(ProposalId),
  sourceRunId: Schema.NullOr(RunId),
  /**
   * Wave C (decision 0071): M-7. Assigned in decide on create from `refCounters.M`, kept on every revision and on
   * restore. Absent on items from before wave C until core `numberMemory` numbers them.
   */
  ref: Schema.optionalKey(ShortRef),
  /** Wave C: written by a person's own Claude Code or Codex over the person MCP ("Jannik via Claude Code"). */
  via: Schema.optionalKey(Schema.Literal("person_mcp")),
  /**
   * Decision 0146: the claim that replaced this one (superseded, never deleted: it stays readable and answers "what
   * was true when"; its `validTo` is when the newer claim took over). Set on the old item by the same commit that
   * creates the new one.
   */
  supersededBy: Schema.optionalKey(MemoryItemId),
  /** Decision 0146: the claim this one replaced (the chain back). */
  supersedes: Schema.optionalKey(MemoryItemId),
  /** Decision 0146: when a person or an agent last re-asserted it against the record (`remember{confirm}`). */
  lastConfirmedAt: Schema.optionalKey(Millis),
})
export type MemoryItem = typeof MemoryItem.Type
// #endregion memory

// #region artifact
/** Blobs in R2 (content-addressed by sha256); external things (PRs) are links. Graph-addressable via EntityRef. */
export const Artifact = Schema.Struct({
  ...RecordMeta,
  ...ContentMeta,
  id: ArtifactId,
  itemId: Schema.NullOr(ItemId),
  runId: Schema.NullOr(RunId),
  kind: ArtifactKind,
  title: Schema.String,
  uri: Schema.String, // "r2://artifacts/<sha256>" or "https://github.com/…/pull/12"
  contentType: Schema.NullOr(Schema.String),
  sizeBytes: Schema.NullOr(Schema.Int),
  sha256: Schema.NullOr(Sha256),
  preview: Schema.NullOr(Schema.String), // short text for cards
  interfaceKey: Schema.NullOr(Slug), // the area interface it fulfils ("keyword-list")
  tainted: Schema.Boolean, // follows the producing run; consuming it via `informs` taints the consumer
  supersedes: Schema.NullOr(ArtifactId),
  state: Schema.Literals(["draft", "final", "published", "rejected"]),
  /** Decision 0059: a watched pull request's state (written by evolve only). */
  pr: Schema.optionalKey(PrView),
  /** Decision 0059: the latest Merge chosen in Minga for this PR (written by evolve only). */
  merge: Schema.optionalKey(MergeView),
  /** Wave K: O-14. Absent on artifacts from before wave K until core `numberOutputs` (or a replay in evolve) numbers them. */
  ref: Schema.optionalKey(ShortRef),
  /**
   * Wave DOC (decision 0068): set on documents (reference material a person added, always project-level), absent on
   * outputs. A version of a document carries it too.
   */
  doc: Schema.optionalKey(DocMeta),
})
export type Artifact = typeof Artifact.Type
// #endregion artifact

// #region comment
/**
 * Threads on any entity. `ask`/`answer` comments are the agent bus: an ask goes to any agent named with `to` (Wave A),
 * else one level up (worker → its item's owner agent, role agent → lead), so information flow is visible on the node.
 * Questions to people are `question` proposals, not comments. An `update` comment carries a health (decision 0035):
 * the latest one on a goal or an item is its health. A `decision` comment (decision 0049) says what was decided, why and
 * where it came from, on the item or goal it changed, when no card carries the why.
 */
export const Comment = Schema.Struct({
  ...RecordMeta,
  ...ContentMeta,
  id: CommentId,
  target: EntityRef,
  threadId: CommentId, // = id for a thread root
  /** Decision 0142: `message` is a message in a thread (`T-n`) that neither asks nor answers. */
  kind: Schema.Literals(["note", "ask", "answer", "system", "update", "decision", "message"]),
  body: Markdown,
  mentions: Schema.Array(Principal), // parsed server-side at write time
  ask: Schema.NullOr(
    Schema.Struct({ to: AgentId, state: Schema.Literals(["open", "answered", "escalated"]) }),
  ),
  answers: Schema.NullOr(CommentId), // answer → the ask it answers
  /** Which mention woke whom, and whether that wake was untrusted. Explains "why did it wake?". */
  wakes: Schema.Array(Schema.Struct({ agentId: AgentId, untrusted: Schema.Boolean })),
  tainted: Schema.Boolean,
  editedAt: Schema.NullOr(Millis), // humans only; agents post corrections
  resolvedAt: Schema.NullOr(Millis),
  /** `update` comments only. */
  health: Schema.optionalKey(Schema.NullOr(Health)),
  /** Decision 0144: a `decision` comment's ref (D-n), minted by decide. Absent on other kinds and older ones. */
  ref: Schema.optionalKey(ShortRef),
  /** Decision 0142: a message's recipients (absent on comments: their mentions say who they reach). */
  to: Schema.optionalKey(Schema.Array(MessageParty)),
  /** Decision 0142: a person's message sent through one of their external participants (`dot`, `claude-code`). */
  via: Schema.optionalKey(Schema.String),
  /** Decision 0142: the sender expects a reply (an ask to an agent is `kind: ask`; to anyone else it is this flag). */
  expectsReply: Schema.optionalKey(Schema.Boolean),
})
export type Comment = typeof Comment.Type
// #endregion comment
